/* eslint-disable react/prop-types */
import { Box, Button, Flex, HStack, Select, Text } from '@chakra-ui/react';
import * as dateFns from 'date-fns';
import { enGB } from 'date-fns/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import { FaChevronDown } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { ProfitabilityAnalysisGrossRevenue } from 'reactRedux/thunks/profitabilityAnalysisThunk/profitabilityAnalysisThunk';
import { customStaticRanges } from 'utils/StaticData';
import useRegions from 'hooks/useRegions';
import DailyProfitabilityGraph from './DailyProfitabilityGraph';
import GrossRevenueProfitabilityGraph from './GrossRevenueProfitabilityGraph';
import RentalProfitableGraph from './RentalProfitableGraph';
import { formatDate } from 'utils/CommonFunction';

const ProfitabilityAnalysisGraph = () => {
  const dispatch = useDispatch();
  const { regions } = useRegions();
  const [showCalendar, setShowCalendar] = useState(false);
  const { profitabilityGrossRevenueGraphData, isLoading } = useSelector(
    (state) => state?.ProfitabilityAnalysisSlice,
  );
  const [dateRange, setDateRange] = React.useState([
    {
      endDate: new Date(),
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      key: 'selection',
    },
  ]);
  const [calendarFilterTitle, setCalendarFilterTitle] =
    useState('Month to Date');
  const [tempCalendarFilterTitle, setTempCalendarFilterTitle] =
    useState('Month to Date');
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [selectedRegion, setSelectedRegion] = useState('');

  const currency =
    regions.find((r) => r.id === +selectedRegion)?.currency ?? 'EUR';

  const handleSelect = (ranges) => {
    const selectedRange = customStaticRanges.find(
      (range) =>
        range.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        range.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );

    setTempCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempDateRange([ranges.selection]);
  };
  const handleApply = async () => {
    setDateRange(tempDateRange);
    const startDate = dateFns.format(tempDateRange[0]?.startDate, 'yyyy-MM-dd');
    const endDate = dateFns.format(tempDateRange[0]?.endDate, 'yyyy-MM-dd');
    setCalendarFilterTitle(tempCalendarFilterTitle);
    dispatch(
      ProfitabilityAnalysisGrossRevenue({
        start: startDate,
        end: endDate,
        ...(selectedRegion && { regionId: selectedRegion }),
      }),
    );
    setShowCalendar(false);
  };

  const handleClear = () => {
    setDateRange(tempDateRange);
    setShowCalendar(false);
  };
  const handleRegionSelect = (event) => {
    const regionId = event.target.value;
    setSelectedRegion(regionId);
    const startDate = dateFns.format(tempDateRange[0]?.startDate, 'yyyy-MM-dd');
    const endDate = dateFns.format(tempDateRange[0]?.endDate, 'yyyy-MM-dd');
    dispatch(
      ProfitabilityAnalysisGrossRevenue({
        start: startDate,
        end: endDate,
        ...(regionId && { regionId: regionId }),
      }),
    );
  };

  useEffect(() => {
    const startDate = dateFns.format(tempDateRange[0]?.startDate, 'yyyy-MM-dd');
    const endDate = dateFns.format(tempDateRange[0]?.endDate, 'yyyy-MM-dd');
    dispatch(
      ProfitabilityAnalysisGrossRevenue({
        start: startDate,
        end: endDate,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regionNameMap = useMemo(() => {
    const map = {};
    regions.forEach((region) => {
      map[region.id] = region.label;
    });
    return map;
  }, [regions]);

  return (
    <>
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <Box
          bg="white"
          p={{ base: '4', md: '5' }}
          templateColumns="repeat(12, 1fr)"
          gap={{ base: '16px', lg: '24px' }}
          boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
          borderRadius="20px"
          border="1px solid #D9D9D9"
          w="full"
        >
          {/* Header and Filters in One Row */}
          <Flex
            justify="space-between"
            align="center"
            flexWrap="wrap"
            gap="20px"
            mb="20px"
          >
            <Text fontSize="2xl" fontWeight="semibold">
              Revenue Overview
            </Text>

            {/* Filters */}
            <HStack
              spacing="0"
              gap="5"
              flexDirection={{ base: 'column', md: 'row' }}
              width={{ base: '100%', md: 'auto' }}
            >
              {/* region filter */}
              <Select
                width={{ base: '100%', md: 'auto' }}
                placeholder="All regions"
                bg="white"
                borderColor="reviveGray"
                color="#292D32"
                fontWeight="semibold"
                borderRadius="8px"
                onChange={handleRegionSelect}
                value={selectedRegion}
              >
                {regions?.map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.label}
                  </option>
                ))}
              </Select>

              <Box
                position="relative"
                flexShrink="0"
                width={{ base: '100%', md: 'auto' }}
              >
                <Button
                  py="10px"
                  px="15"
                  fontWeight="semibold"
                  type="button"
                  bg="white"
                  border="1px solid"
                  borderColor="reviveGray"
                  color="#292D32"
                  borderRadius="8px"
                  onClick={() => setShowCalendar(!showCalendar)}
                  justifyContent="space-between"
                  width={{ base: '100%', md: 'auto' }}
                >
                  {calendarFilterTitle}{' '}
                  <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
                </Button>
                {/* Date Range Picker Dropdown */}
                {showCalendar && (
                  <>
                    <Box
                      position="fixed"
                      bg="rgb(0 0 0 / 40%)"
                      top="0"
                      left="0"
                      right="0"
                      bottom="0"
                      zIndex="9"
                      onClick={() => setShowCalendar(false)}
                    ></Box>
                    <Box
                      p="32px"
                      mt="8px"
                      position="absolute"
                      right="0"
                      zIndex="10"
                      bg="white"
                      boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                      borderRadius="20px"
                      className="custom-date-picker"
                    >
                      <DateRangePicker
                        ranges={tempDateRange}
                        onChange={handleSelect}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        months={2}
                        direction="horizontal"
                        locale={enGB}
                        minDate={new Date(2000, 0, 1)}
                        maxDate={new Date()}
                        staticRanges={customStaticRanges}
                        inputRanges={[]}
                      />
                      <Flex justifyContent="right" gap="12px" marginTop="30px">
                        <Button
                          className="clear-btn"
                          border="1px solid"
                          borderColor="reviveGray"
                          borderRadius="10px"
                          onClick={handleClear}
                        >
                          Clear
                        </Button>
                        <Button
                          className="apply-btn"
                          bg="#6658fd"
                          border="1px solid"
                          borderColor="#6658fd"
                          color="white"
                          borderRadius="10px"
                          _hover={{
                            bg: 'transparent',
                            color: '#6658fd',
                          }}
                          onClick={handleApply}
                        >
                          Apply
                        </Button>
                      </Flex>
                    </Box>
                  </>
                )}
              </Box>
            </HStack>
          </Flex>
          <Box
            mb={'20px'}
            fontWeight="semibold"
            color="primary"
            display="flex"
            flexWrap="wrap"
          >
            <Text>{`Selected Period : ${formatDate(dateRange[0].startDate)} - ${formatDate(dateRange[0].endDate)}`}</Text>
          </Box>
          {/* Graphs section */}
          <GrossRevenueProfitabilityGraph
            isLoading={isLoading}
            graphData={profitabilityGrossRevenueGraphData?.revenuePerMerchant}
            regionNameMap={regionNameMap}
            currency={currency}
          />

          {/* Rental Graph */}
          <RentalProfitableGraph
            rentalData={profitabilityGrossRevenueGraphData?.revenuePerMerchant}
            isLoading={isLoading}
            regionNameMap={regionNameMap}
            currency={currency}
          />

          {/* Daily Profitability Graph */}
          <DailyProfitabilityGraph
            dailyData={profitabilityGrossRevenueGraphData?.overview}
            isLoading={isLoading}
            regionNameMap={regionNameMap}
            currency={currency}
          />
        </Box>
      </Box>
    </>
  );
};

export default ProfitabilityAnalysisGraph;
